import React, { FC } from "react";
import { connect } from "react-redux";

import { faFlag } from "@fortawesome/pro-solid-svg-icons/faFlag";
import { faFlagCheckered } from "@fortawesome/pro-solid-svg-icons/faFlagCheckered";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import Trip from "@mapmycustomers/shared/types/entity/Trip";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";
import { ButtonLink } from "@mapmycustomers/ui";

import localSettings from "@app/config/LocalSettings";
import { showEntityView } from "@app/store/entityView/actions";
import { meterDistanceFormatter } from "@app/util/fieldModel/impl/fieldUtil";
import { formatCoordinates, formatRawDate } from "@app/util/formatters";

import styles from "./RouteDetails.module.scss";

const getGoogleMapsUrlForAddress = (address: string) => {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
};

const getGoogleMapsUrlForLocation = ([long, lat]: LongLat) => {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${long}`;
};

export interface RouteDetailsProps {
  showAmounts: boolean;
  trip: Trip;
}

interface Props extends RouteDetailsProps {
  onEntityView: typeof showEntityView;
}

const RouteDetails: FC<Props> = ({ onEntityView, showAmounts, trip }) => {
  const intl = useIntl();
  const currencyCode = localSettings.getCurrencyCode();

  const startGeoPoint = trip.startGeopoint;
  const startAddress = trip.startAddress;
  const startEntity = trip.startEntity;

  const endGeoPoint = trip.endGeopoint;
  const endAddress = trip.endAddress;
  const endEntity = trip.endEntity;
  const hasEnd = !!(trip.endTime && (endGeoPoint || endAddress || endEntity));

  return (
    <div>
      <div className={styles.stop}>
        <FontAwesomeIcon icon={faFlag} />
        <div>
          {intl.formatMessage(
            {
              id: "reports.mileageTracking.preview.details.start",
              defaultMessage: "Started at {time}",
              description: "Started at section on the Trip preview pane",
            },
            { time: formatRawDate(trip.startTime, "p") }
          )}
        </div>

        <div className={styles.stopLocation}>
          {startEntity ? (
            <ButtonLink
              onClick={() =>
                onEntityView({
                  entityId: startEntity!.id,
                  entityType: startEntity!.entity,
                })
              }
            >
              {startEntity.name}
            </ButtonLink>
          ) : startAddress ? (
            <a href={getGoogleMapsUrlForAddress(startAddress)} rel="noreferrer" target="_blank">
              {startAddress}
            </a>
          ) : (
            <a
              href={getGoogleMapsUrlForLocation(startGeoPoint.coordinates)}
              rel="noreferrer"
              target="_blank"
            >
              {formatCoordinates(startGeoPoint.coordinates)}
            </a>
          )}
        </div>
      </div>
      {hasEnd && (
        <div className={styles.travel}>
          {showAmounts && (
            <div>
              {intl.formatNumber(trip.totalCost, {
                currency: currencyCode,
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
                style: "currency",
              })}
            </div>
          )}
          {isNotEmpty(trip.distance) && (
            <>
              {showAmounts && "•"}
              <div>{meterDistanceFormatter({}, trip.distance)}</div>
            </>
          )}
        </div>
      )}
      {hasEnd && (
        <div className={styles.stop}>
          <FontAwesomeIcon icon={faFlagCheckered} />
          <div>
            {intl.formatMessage(
              {
                id: "reports.mileageTracking.preview.details.end",
                defaultMessage: "Ended at {time}",
                description: "Ended at section on the Trip preview pane",
              },
              { time: formatRawDate(trip.endTime, "p") }
            )}
          </div>
          <div className={styles.stopLocation}>
            {endEntity ? (
              <ButtonLink
                onClick={() =>
                  onEntityView({
                    entityId: endEntity!.id,
                    entityType: endEntity!.entity,
                  })
                }
              >
                {endEntity.name}
              </ButtonLink>
            ) : endAddress ? (
              <a href={getGoogleMapsUrlForAddress(endAddress)} rel="noreferrer" target="_blank">
                {endAddress}
              </a>
            ) : (
              // we know endGeopoint exists as long as trip has ended
              <a
                href={getGoogleMapsUrlForLocation(endGeoPoint!.coordinates)}
                rel="noreferrer"
                target="_blank"
              >
                {formatCoordinates(endGeoPoint!.coordinates)}
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const mapDispatchToProps = {
  onEntityView: showEntityView,
};

export default connect(null, mapDispatchToProps)(RouteDetails);
